import React, { useEffect, useState } from 'react';
import {
  CreateBase,
  SimpleForm,
  TextInput,
  BooleanInput,
  useNotify,
  useRedirect,
  useCreate,
  required,
  useDataProvider,
  SelectInput
} from "react-admin";
import Box from "@mui/material/Box";
import { useFormContext } from 'react-hook-form';

const UserCreate: React.FC = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const [create] = useCreate();
  const formContext = useFormContext();
  const setValue = formContext?.setValue;
  
  const [roles, setRoles] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  // ----------------------
  // 加载角色和部门列表
  // ----------------------
  useEffect(() => {
    setLoading(true);

    Promise.all([
      dataProvider.getList('permissionRoles', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {}
      }),
      dataProvider.getList('permissionDepartment', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {}
      })
    ])
      .then(([rolesRes, departmentsRes]) => {
        setRoles(rolesRes.data);
        setDepartments(departmentsRes.data);
        setLoading(false);
      })
      .catch((e) => {
        console.error(e);
        notify('Error loading data', { type: 'warning' });
        setLoading(false);
      });
  }, [dataProvider, notify]);

  useEffect(() => {
    if (!selectedDepartmentId) {
      setPositions([]);
      return;
    }

    dataProvider
      .getList('permissionPosition', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: { departmentId: selectedDepartmentId },
      })
      .then((res: any) => {
        setPositions(res.data);
      })
      .catch((e: any) => {
        console.error(e);
        notify('Error loading positions', { type: 'warning' });
        setPositions([]);
      });
  }, [selectedDepartmentId, dataProvider, notify]);

  if (loading) return <span>Loading...</span>;

  // ----------------------
  // 保存操作
  // ----------------------
  const handleSave = async (data: any) => {
    try {
      let password = data.password;
      
      // 使用 Web Crypto 做 SHA-256 哈希
      const enc = new TextEncoder();
      const pwBuf = enc.encode(password);
      const hashBuf = await crypto.subtle.digest('SHA-256', pwBuf);
      const hashArray = Array.from(new Uint8Array(hashBuf));
      const passwordHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      // 立即清除明文密码引用
      password = undefined;

      const payload = {
        userName: data.userName,
        email: data.email,
        password: passwordHash, // 使用加密后的密码
        departmentId: data.departmentId,
        departmentPositionId: data.departmentPositionId,
        roleId: data.roleId,
        isActive: data.isActive !== undefined ? data.isActive : true,
      };

      await create('permissionUsers', { data: payload });
      // console.log(payload);
      notify('User created successfully', { type: 'success' });
      setTimeout(() => {
        redirect('list', 'permissionUsers');
      }, 500);
    } catch (error) {
      console.error('Create user error:', error);
      notify('Create failed', { type: 'error' });
    }
  };

  return (
    <CreateBase resource="permissionUsers">
      <SimpleForm sx={{ backgroundColor: '#ffffff', padding: '36px' }} onSubmit={handleSave}>
        <Box display="flex" flexWrap="wrap" gap={2}>

          {/* 邮箱 */}
          <Box flex="1 1 48%">
            <TextInput
              source="email"
              label="Email"
              fullWidth
              validate={[required()]}
            />
          </Box>

          {/* 用户名（自动填充） */}
          <Box flex="1 1 48%">
            <TextInput
              source="userName"
              label="User Name"
              fullWidth
              validate={[required()]}
            />
          </Box>


          {/* 密码 */}
          <Box flex="1 1 48%">
            <TextInput
              source="password"
              label="Password"
              type="password"
              fullWidth
              validate={[required()]}
            />
          </Box>

          {/* 部门下拉列表 */}
          <Box flex="1 1 48%">
            <SelectInput
              source="departmentId"
              label="Department"
              choices={departments}
              optionText="departmentName"
              optionValue="id"
              fullWidth
              validate={[required()]}
              onChange={(event) => {
                const value = event.target.value;
                const deptId = typeof value === 'string' ? Number(value) : value;
                setSelectedDepartmentId(deptId);
                if (setValue) setValue('departmentPositionId', '');
              }}
            />
          </Box>

          {/* 职级下拉列表（根据部门动态加载） */}
          <Box flex="1 1 48%">
            <SelectInput
              source="departmentPositionId"
              label="Position Title"
              choices={positions}
              optionText="positionName"
              optionValue="id"
              fullWidth
              validate={[required()]}
              disabled={!selectedDepartmentId || positions.length === 0}
            />
          </Box>

          {/* 角色下拉列表 */}
          <Box flex="1 1 48%">
            <SelectInput
              source="roleId"
              label="Role"
              choices={roles}
              optionText="roleName"
              optionValue="id"
              fullWidth
              validate={[required()]}
            />
          </Box>

          {/* 启用状态 */}
          <Box flex="1 1 48%">
            <BooleanInput
              source="isActive"
              label="Active"
              defaultValue={true}
              fullWidth
            />
          </Box>

        </Box>
      </SimpleForm>
    </CreateBase>
  );
};

export default UserCreate;
